import type { AudioControlPort, AudioPresentationPort, AudioService } from "../ports/AudioService";
import type { AudioBusId, AudioEvent, AudioPlayOpts, AudioSpeedMode, FeatureAudioState } from "../ports/types";
import type { AudioEventMapping } from "../banks/types";

/** One call the game made on the audio service, in order. Formatted for e2e by `formatAudioLogEntry`. */
export type AudioLogEntry =
  | { kind: "preload" }
  | { kind: "unlock" }
  | { kind: "emit"; id: string; sound: string | null }
  | { kind: "play"; id: string }
  | { kind: "sfx_loop_start"; id: string }
  | { kind: "sfx_loop_stop"; id: string }
  | { kind: "sfx_loop_gain"; id: string; scale: number }
  | { kind: "music"; id: string }
  | { kind: "music_intro"; intro: string; loop: string }
  | { kind: "feature_state"; state: FeatureAudioState }
  | { kind: "ambient"; id: string | null }
  | { kind: "bus"; bus: AudioBusId; enabled: boolean }
  | { kind: "master_volume"; volume: number }
  | { kind: "duck"; amount: number | null }
  | { kind: "unduck" }
  | { kind: "slam" }
  | { kind: "speed"; mode: AudioSpeedMode };

/** `emit:spin.start→spin_start`, `play:column_drop`, `music:music_feature_idle`, `duck:0.35`, `slam` … */
export function formatAudioLogEntry(entry: AudioLogEntry): string {
  switch (entry.kind) {
    case "preload":
    case "unlock":
    case "unduck":
    case "slam":
      return entry.kind;
    case "emit":
      return `emit:${entry.id}→${entry.sound ?? "∅"}`;
    case "play":
    case "sfx_loop_start":
    case "sfx_loop_stop":
    case "music":
      return `${entry.kind}:${entry.id}`;
    case "sfx_loop_gain":
      return `sfx_loop_gain:${entry.id}=${entry.scale}`;
    case "music_intro":
      return `music_intro:${entry.intro}→${entry.loop}`;
    case "feature_state":
      return `feature_state:${entry.state ?? "null"}`;
    case "ambient":
      return `ambient:${entry.id ?? "null"}`;
    case "bus":
      return `bus:${entry.bus}=${entry.enabled ? "on" : "off"}`;
    case "master_volume":
      return `master_volume:${entry.volume}`;
    case "duck":
      return `duck:${entry.amount ?? "default"}`;
    case "speed":
      return `speed:${entry.mode}`;
    default: {
      const exhaustive: never = entry;
      return exhaustive;
    }
  }
}

/**
 * Dev-only decorator over the real `AudioService`: forwards every call unchanged and records it,
 * so the debug hook can hand the e2e suite the exact id sequence a round asked for (the
 * done-when). Records what the GAME requested — drops and voice stealing inside the engine stay
 * visible through `control.__getDebugSnapshot()`. Optional presentation methods are exposed only
 * when the wrapped service has them, so `startSfxLoop?.()` feature checks keep their meaning.
 */
export class RecordingAudioService implements AudioService {
  public readonly control: AudioControlPort;
  public readonly presentation: AudioPresentationPort;
  public readonly entries: AudioLogEntry[] = [];

  public constructor(inner: AudioService, mapping: AudioEventMapping) {
    const record = (entry: AudioLogEntry): void => {
      this.entries.push(entry);
    };
    const control = inner.control;
    this.control = {
      preloadSounds: () => {
        record({ kind: "preload" });
        control.preloadSounds();
      },
      unlockFromUserGesture: () => {
        record({ kind: "unlock" });
        control.unlockFromUserGesture();
      },
      setSpeedMode: (mode) => {
        record({ kind: "speed", mode });
        control.setSpeedMode(mode);
      },
      setFeatureState: (state) => {
        record({ kind: "feature_state", state });
        control.setFeatureState(state);
      },
      setMusicTrack: (id, fadeMs) => {
        record({ kind: "music", id });
        control.setMusicTrack(id, fadeMs);
      },
      playMusicWithIntro: (introId, loopId) => {
        record({ kind: "music_intro", intro: introId, loop: loopId });
        control.playMusicWithIntro(introId, loopId);
      },
      setBusEnabled: (bus, enabled) => {
        record({ kind: "bus", bus, enabled });
        control.setBusEnabled(bus, enabled);
      },
      setMasterVolume: (volume) => {
        record({ kind: "master_volume", volume });
        control.setMasterVolume(volume);
      },
      setAmbientTrack: (id, fadeMs) => {
        record({ kind: "ambient", id });
        control.setAmbientTrack(id, fadeMs);
      },
      duckMusic: (amount, fadeMs) => {
        record({ kind: "duck", amount: amount ?? null });
        control.duckMusic(amount, fadeMs);
      },
      unduckMusic: (fadeMs) => {
        record({ kind: "unduck" });
        control.unduckMusic(fadeMs);
      },
      notifySlam: () => {
        record({ kind: "slam" });
        control.notifySlam();
      },
      __getDebugSnapshot: () => control.__getDebugSnapshot?.(),
    };

    const presentation = inner.presentation;
    const recording: AudioPresentationPort = {
      emit: (event: AudioEvent) => {
        record({ kind: "emit", id: event.id, sound: mapping[event.id] ?? null });
        presentation.emit(event);
      },
    };
    if (presentation.playSoundId) {
      recording.playSoundId = (soundId: string, opts?: AudioPlayOpts) => {
        record({ kind: "play", id: soundId });
        presentation.playSoundId!(soundId, opts);
      };
    }
    if (presentation.startSfxLoop) {
      recording.startSfxLoop = (soundId: string) => {
        record({ kind: "sfx_loop_start", id: soundId });
        presentation.startSfxLoop!(soundId);
      };
    }
    if (presentation.stopSfxLoop) {
      recording.stopSfxLoop = (soundId: string) => {
        record({ kind: "sfx_loop_stop", id: soundId });
        presentation.stopSfxLoop!(soundId);
      };
    }
    if (presentation.setSfxLoopGainScale) {
      recording.setSfxLoopGainScale = (soundId: string, scale: number) => {
        record({ kind: "sfx_loop_gain", id: soundId, scale });
        presentation.setSfxLoopGainScale!(soundId, scale);
      };
    }
    this.presentation = recording;
  }

  public clear(): void {
    this.entries.length = 0;
  }
}
